import { Chip } from "@coherehealth/common";
import { Box, Tooltip } from "@mui/material";

interface ChipStyleProps {
  customBackgroundColor?: string;
  customLabelColor?: string;
}

interface OverflowChipCellProps {
  items: string[];
  maxVisible?: number;
  chipStyle?: ChipStyleProps;
}

// Table badges match the edit-drawer chips but without the border and with only 4px of padding
// around the label (Figma 8619:54232).
const badgeStyle = { paddingLeft: 4, paddingRight: 4, borderRadius: 4, fontSize: 14 } as const;

export default function OverflowChipCell({ items, maxVisible = 2, chipStyle }: OverflowChipCellProps) {
  if (!items || items.length === 0) {
    return <span>--</span>;
  }

  const visible = items.slice(0, maxVisible);
  const overflow = items.slice(maxVisible);
  // Collapsed rows (finite maxVisible) stay on one line and clip instead of wrapping; expanded rows wrap.
  const collapsed = Number.isFinite(maxVisible);

  return (
    <Box
      sx={{
        display: "flex",
        gap: "6px",
        flexWrap: collapsed ? "nowrap" : "wrap",
        alignItems: "center",
        minWidth: 0,
        overflow: collapsed ? "hidden" : "visible",
      }}
    >
      {visible.map((item, idx) => (
        <Chip
          key={`${item}-${idx}`}
          label={item}
          size="small"
          style={{ ...badgeStyle, flexShrink: collapsed ? 1 : 0, minWidth: 0 }}
          {...chipStyle}
        />
      ))}
      {overflow.length > 0 && (
        <Tooltip
          title={
            overflow.length > 10
              ? `${overflow.slice(0, 10).join(", ")}... and ${overflow.length - 10} more`
              : overflow.join(", ")
          }
          placement="top"
        >
          <Chip
            label={`+${overflow.length}`}
            size="small"
            data-testid="overflow-chip"
            aria-label={`${overflow.length} more: ${
              overflow.length > 10
                ? `${overflow.slice(0, 10).join(", ")} and ${overflow.length - 10} more`
                : overflow.join(", ")
            }`}
            style={{ ...badgeStyle, flexShrink: 0 }}
            {...chipStyle}
          />
        </Tooltip>
      )}
    </Box>
  );
}
